/**
 * @file Emotion.jsx
 * @description Main page component for the Emotion insight module.
 *              Fetches emotion data by date range and channel (Texts / Emails),
 *              displays KPI summary cards, horizontal bar charts for emotion % and count,
 *              and a pivoted detail table grouped by date.
 *
 * @component Emotion
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Bar, BarChart, CartesianGrid, Cell,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchEmotionData,
  fetchEmotionConversations,
} from "../../../services/insightModule/insightService";
import DatePickerComponent from "../../../common/components/DatePickerComponent";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PURPLE = "#7F77DD";
const TEAL   = "#0F6E56";
const GRAY   = "#888780";

const EMOTION_COLORS = [
  BLUE, PINK, GREEN, AMBER, PURPLE, TEAL, GRAY,
  "#185FA5","#0F6E56","#993556","#3C3489","#633806",
];

// ── Helpers ────────────────────────────────────────────────────────
// Parse YYYY-MM-DD as local date, not UTC.
// new Date("2026-04-25") is UTC midnight → lands on the previous day in
// negative-offset timezones.
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

// Build a stable YYYY-MM-DD key from a date string, in local time.
function localDateKey(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return "Unknown";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE }) {
  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 14px", minWidth: 0, flex: 1,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Emotion = () => {
  const [startDate, setStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [channel,   setChannel]   = useState("Texts");
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");

  // Drill-down dialog state. null = closed; object = open w/ that config.
  const [drill, setDrill] = useState(null);

  const load = useCallback(async (start, end, ch) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchEmotionData({
        startDate:   start ? start.toISOString() : null,
        endDate:     end   ? end.toISOString()   : null,
        channeltype: ch,
      });
      setData(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load emotion data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(startDate, endDate, "Texts");
    // load on mount only; date defaults are stable via lazy initializer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Aggregates ─────────────────────────────────────────────────
  const { emotionTotals, grandTotal, pivotedRows, allEmotions } = useMemo(() => {
    if (!data.length) return { emotionTotals: [], grandTotal: 0, pivotedRows: [], allEmotions: [] };

    const emotionMap = {};
    let grand = 0;
    data.forEach(r => {
      const em = (r.Emotion || "").trim();
      if (!em) return;
      const c = safeNum(r.Count);
      emotionMap[em] = (emotionMap[em] || 0) + c;
      grand += c;
    });

    const totals = Object.entries(emotionMap)
      .map(([name, count]) => ({ name, count, pct: grand > 0 ? count / grand * 100 : 0 }))
      .sort((a, b) => b.count - a.count);

    const dateMap = {};
    const emotionsSet = new Set();
    data.forEach(r => {
      const em = (r.Emotion || "").trim();
      if (!em) return;
      emotionsSet.add(em);
      const key = localDateKey(r.ConvDate);
      const ts  = parseLocalDate(r.ConvDate)?.getTime() || 0;
      if (!dateMap[key]) dateMap[key] = { ConvDate: r.ConvDate, _ts: ts, Total: 0, emotions: {} };
      dateMap[key].emotions[em] = (dateMap[key].emotions[em] || 0) + safeNum(r.Count);
      dateMap[key].Total += safeNum(r.Count);
    });

    const pivoted = Object.values(dateMap)
      .sort((a, b) => b._ts - a._ts);
    const sortedEmotions = Array.from(emotionsSet).sort();

    return { emotionTotals: totals, grandTotal: grand, pivotedRows: pivoted, allEmotions: sortedEmotions };
  }, [data]);

  // ── Dynamic column width based on longest emotion name ─────────
  const emotionColWidth = useMemo(() =>
    Math.max(90, ...allEmotions.map(em => em.length * 10 + 24))
  , [allEmotions]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // ── Drill-down ─────────────────────────────────────────────────
  // Build a drill config from the click context.
  //   emotion only       → all conversations w/ that emotion in the picker range
  //   date only          → all conversations on that date (any emotion)
  //   emotion + date     → conversations w/ that emotion on that single date
  const openDrill = useCallback(({ emotion = null, date = null }) => {
    const emotionIdx = emotion ? allEmotions.indexOf(emotion) : -1;
    const emotionColor = emotionIdx >= 0
      ? EMOTION_COLORS[emotionIdx % EMOTION_COLORS.length]
      : BLUE;

    const title =
      emotion && date ? "Conversations by emotion & date" :
      emotion         ? `Conversations with emotion: ${emotion}` :
                        `Conversations on ${fmtDate(date)}`;

    // single-day window when drilling a row, else use the picker range
    const dStart = date ? new Date(date) : startDate;
    const dEnd   = date ? new Date(date) : endDate;

    setDrill({
      title,
      metric: emotion ? { label: "Emotion", value: emotion, color: emotionColor } : null,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate) },
      ].filter(Boolean),
      channel,
      emotionColors: Object.fromEntries(                                       // NEW
        allEmotions.map((em, i) => [em, EMOTION_COLORS[i % EMOTION_COLORS.length]])
      ),
      fetchData: () => fetchEmotionConversations({
        emotion,
        startDate:   dStart ? dStart.toISOString() : null,
        endDate:     dEnd   ? dEnd.toISOString()   : null,
        channeltype: channel,
      }),
    });
  }, [allEmotions, startDate, endDate, channel]);

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d && (!startDate || d >= startDate)) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, channel)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => {
              setChannel(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiCard label="Total records"   value={grandTotal.toLocaleString()} color={BLUE}   />
            <KpiCard label="Unique emotions" value={allEmotions.length}          color={PURPLE} />
            {emotionTotals.slice(0, 3).map((e, i) => (
              <KpiCard
                key={e.name}
                label={e.name}
                value={`${e.count.toLocaleString()} (${e.pct.toFixed(1)}%)`}
                color={EMOTION_COLORS[i % EMOTION_COLORS.length]}
              />
            ))}
          </Box>

          {/* ── EMOTION % HORIZONTAL BAR ──────────────────────── */}
          <SectionTitle>Emotion distribution %</SectionTitle>

          <ChartCard height={Math.max(220, emotionTotals.length * 44)}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={emotionTotals}
                layout="vertical"
                margin={{ top: 4, right: 60, bottom: 4, left: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} domain={[0, 100]}
                  tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name"
                  tick={{ fontSize: 11, fill: "#555" }} width={130} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, p) => [
                    `${p.payload.pct.toFixed(2)}% (${p.payload.count.toLocaleString()} / ${grandTotal.toLocaleString()})`,
                    "Emotion"
                  ]}
                />
                <Bar
                  dataKey="pct"
                  name="%"
                  radius={[0,4,4,0]}
                  cursor="pointer"
                  onClick={(entry) => entry?.name && openDrill({ emotion: entry.name })}
                >
                  {emotionTotals.map((_, i) => (
                    <Cell key={i} fill={EMOTION_COLORS[i % EMOTION_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── EMOTION COUNT HORIZONTAL BAR ──────────────────── */}
          <SectionTitle>Emotion count</SectionTitle>

          <ChartCard height={Math.max(220, emotionTotals.length * 44)}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={emotionTotals}
                layout="vertical"
                margin={{ top: 4, right: 60, bottom: 4, left: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} />
                <YAxis type="category" dataKey="name"
                  tick={{ fontSize: 11, fill: "#555" }} width={130} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, p) => [
                    `${v.toLocaleString()} (${p.payload.pct.toFixed(1)}%)`,
                    "Count"
                  ]}
                />
                <Bar
                  dataKey="count"
                  name="Count"
                  radius={[0,4,4,0]}
                  cursor="pointer"
                  onClick={(entry) => entry?.name && openDrill({ emotion: entry.name })}
                >
                  {emotionTotals.map((_, i) => (
                    <Cell key={i} fill={EMOTION_COLORS[i % EMOTION_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE — PIVOTED BY DATE ──────────────────── */}
          <SectionTitle>Detail by date</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "auto",
          }}>
            {/* Header — extra 40px column at the front for the drill icon */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: `40px 100px repeat(${allEmotions.length}, ${emotionColWidth}px) 80px`,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1, minWidth: "max-content",
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              <Box />
              <Box>Date</Box>
              {allEmotions.map((em, i) => (
                <Box key={em} sx={{
                  textAlign: "right",
                  color: EMOTION_COLORS[i % EMOTION_COLORS.length],
                }}>
                  {em}
                </Box>
              ))}
              <Box sx={{ textAlign: "right" }}>Total</Box>
            </Box>

            {/* Rows */}
            {pivotedRows.map((row, i) => (
              <Box key={i} sx={{
                display: "grid",
                gridTemplateColumns: `40px 100px repeat(${allEmotions.length}, ${emotionColWidth}px) 80px`,
                px: 2, py: "6px",
                fontSize: 12, minWidth: "max-content",
                borderBottom: "0.5px solid", borderColor: "divider",
                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                "&:hover": { background: "rgba(55,138,221,0.06)" },
                alignItems: "center",
              }}>
                {/* drill all emotions for this date */}
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => openDrill({ date: row.ConvDate })}
                    sx={{ width: 24, height: 24 }}
                    title="View conversations for this date"
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <Box>{fmtDate(row.ConvDate)}</Box>
                {allEmotions.map((em, j) => {
                  const count = row.emotions[em] || 0;
                  const clickable = count > 0;
                  return (
                    <Box
                      key={em}
                      onClick={clickable
                        ? () => openDrill({ emotion: em, date: row.ConvDate })
                        : undefined}
                      sx={{
                        textAlign: "right",
                        cursor: clickable ? "pointer" : "default",
                        color: clickable
                          ? EMOTION_COLORS[j % EMOTION_COLORS.length]
                          : "text.disabled",
                        fontWeight: clickable ? 500 : 400,
                        "&:hover": clickable ? { textDecoration: "underline" } : {},
                      }}
                    >
                      {count}
                    </Box>
                  );
                })}
                <Box sx={{ textAlign: "right", fontWeight: 500 }}>
                  {row.Total.toLocaleString()}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* ── Drill-down popup ───────────────────────────────────── */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default Emotion;